import { IAppContext, RestrictedEvents, ServiceRequestType } from "app-context";
import { useInitialContextValues } from "./useInitialContextValues";
import { getUniqueArray } from "methods/getUniqueArray";
import { useEffect, useState } from "react";
import { useCookies } from "react-cookie";

//Constants
import { COOKIES, DefaultCookieOptions } from "_constants/misc";
import { GenericObject } from "../custom-types/generic-object";
import { useRouter } from "next/router";
import { useGetCurrentLocationOnPageLoad } from "./useGetCurrentLocation";
import { LocationSearchSource } from "../_services/city-search.service";
import useMediaQuery from "./useMediaQuery";

interface UseSetAppContextProps {
    src?: "csDashboard" | "client";
}

export function useSetAppContext({ src }: UseSetAppContextProps = {}) {
    const ENV = process.env.REACT_APP_ENV || process.env.NEXT_PUBLIC_REACT_APP_ENV;
    const isProductionEnv = ENV === "production";
    const router = useRouter();
    const [cookies, setCookie, removeCookie] = useCookies<string>([]);
    const isMobileScreen = useMediaQuery({ queryType: "mobile" });
    const initialContextValues = useInitialContextValues();

    const [context, setContext] = useState<IAppContext>({
        ...initialContextValues,
        ...(src === "csDashboard" && {
            isCsDashboardUser: true
        }),
        isProductionEnv
    });
    const currentCity = useGetCurrentLocationOnPageLoad({
        enabled: src !== "csDashboard",
        locationSearchParams: {
            source: LocationSearchSource.CompanyCurrentLocation
        }
    });

    async function getKycMethodTypes(url: string) {
        try {
            return await fetch(url).then((res) => {
                res.json().then((data) => {
                    setContext((state: IAppContext) => ({
                        ...state,
                        kycVerificationMethodTypes: data
                    }));
                });
            });
        } catch (err) {
            console.error("Error - ", err);
        }
    }


    type EventCountKey = keyof NonNullable<IAppContext["eventCounts"]>;

    const updateEventCount = (name: RestrictedEvents, value?: number) => {
        if (name === "pagination") {
            return;
        }
        const countKey = name as EventCountKey;
        const { eventCounts } = context;
        const newValue = Number(value ?? context?.eventCounts?.[countKey] ?? 0) + 1;

        if (eventCounts?.[countKey] !== undefined) {
            eventCounts[countKey] = newValue;
        }

        setCookie(COOKIES[countKey], newValue);
        setContext((state) => ({ ...state, eventCounts }));
    };

    const isUserLoggedIn = !!(context.customerCompanyId || context.companyId);

    const checkRestrictedEvent = (name: RestrictedEvents) => {
        switch (name) {
            case "citySearchCount":
            case "transporterSearchCount": {
                const count = Number(
                    context?.eventCounts?.[name as EventCountKey] || 0
                );
                return count >= 3;
            }
            case "pagination": {
                return true;
            }
            default: {
                return false;
            }
        }
    };

    const checkAndHandleGuestUserRestriction = (name: RestrictedEvents) => {
        if (!isUserLoggedIn) {
            if (checkRestrictedEvent(name)) {
                router?.push({ hash: "#Login" });
                return false;
            }
            updateEventCount?.(name);
        }
        return true;
    };

    useEffect(() => {
        setContext((context) => ({
            ...context,
            isMobileScreen
        }));
    }, [isMobileScreen]);

    useEffect(() => {
        const fetchCurrentLocation = async () => {
            if (currentCity?.place_id) {
                setContext((context) => ({
                    ...context,
                    globalData: {
                        ...context.globalData,
                        userPlaceInfo: currentCity
                    }
                }));
            }
        };
        fetchCurrentLocation();
    }, [currentCity]);

    useEffect(() => {
        setContext((state) => ({
            ...state,
            isAuthReady: true,
            phoneNum: cookies.phoneNum,
            name: cookies.name,
            userId: cookies.ui,
            companyId: cookies[COOKIES.companyId],
            companyLocation: cookies[COOKIES.companyLocation],
            companyLogo: cookies.cLogo,
            isPhoneVerified: !!+cookies.vv,
            customerCompanyId: cookies[COOKIES.customerCompanyId],
            transporterContactsRqstd: cookies.tcr || [],
            kycPopupInitialLoad: cookies.kip || 0,
            historyLocation: cookies.hl,
            eventCounts: {
                citySearchCount: cookies[COOKIES.citySearchCount] ?? 0,
                transporterSearchCount: cookies[COOKIES.transporterSearchCount] ?? 0
            },
            thirdPartyCompanyName: cookies[COOKIES.thirdPartyCompanyName] ?? "",
            isMobile: window.matchMedia("(max-width: 991px)").matches,
            updateHeaderClassName: (className) => {
                setContext((state) => ({ ...state, headerClassName: className }));
            },
            setCurrentLocation: (loc) => setContext((state) => ({ ...state, currentLocation: loc })),
            recentTransporterCitySearches: cookies.rtcs,
            updateRecentTransporterCitySearches: (city) => {
                city &&
                    setContext((state) => {
                        const updatedRecents = getUniqueArray(
                            [
                                { ...city, category: "recent", categoryLabel: "RECENT SEARCHES" } as any,
                                ...(state.recentTransporterCitySearches || [])
                            ],
                            "id"
                        ).slice(0, 5);
                        setCookie(
                            COOKIES.recentTransporterCitySearches,
                            JSON.stringify(updatedRecents),
                            DefaultCookieOptions
                        );
                        return {
                            ...state,
                            recentTransporterCitySearches: updatedRecents
                        };
                    });
            },
            setPhoneNum: (phoneNum) => {
                if (phoneNum) {
                    setCookie("phoneNum", phoneNum, DefaultCookieOptions);
                    setContext((state) => ({ ...state, phoneNum }));
                }
            },
            setName: (name) => {
                if (name) {
                    setCookie("name", name, DefaultCookieOptions);
                } else {
                    removeCookie("name", DefaultCookieOptions);
                }
                setContext((state) => ({ ...state, name }));
            },
            setUserId: (userId) => {
                if (userId) {
                    setCookie(COOKIES.userId, userId, DefaultCookieOptions);
                } else {
                    removeCookie(COOKIES.userId, DefaultCookieOptions);
                }
                setContext((state) => ({ ...state, userId: userId }));
            },
            setCompanyId: (companyId) => {
                if (companyId) {
                    setCookie(COOKIES.companyId, companyId, DefaultCookieOptions);
                } else {
                    removeCookie(COOKIES.companyId, DefaultCookieOptions);
                }
                setContext((state) => ({ ...state, companyId }));
            },
            setCompanyLocation: (companyLocation) => {
                if (companyLocation) {
                    setCookie(COOKIES.companyLocation, companyLocation, DefaultCookieOptions);
                } else {
                    removeCookie(COOKIES.companyLocation, DefaultCookieOptions);
                }
                setContext((state) => ({ ...state, companyLocation }));
            },
            setCompanyLogo: (logo) => {
                if (logo) {
                    setCookie(COOKIES.companyLogo, logo, DefaultCookieOptions);
                } else {
                    removeCookie(COOKIES.companyLogo, DefaultCookieOptions);
                }
                setContext((state) => ({ ...state, companyLogo: logo }));
            },
            setIsPhoneVerified: (isVerified) => {
                if (isVerified) {
                    setCookie("vv", 1, {
                        ...DefaultCookieOptions
                    });
                } else {
                    removeCookie("vv", DefaultCookieOptions);
                }
                setContext((state) => ({ ...state, isPhoneVerified: isVerified }));
            },

            isEmailVerified: !!+cookies.ev,
            setIsEmailVerified: (isEmailVerified) => {
                if (isEmailVerified) {
                    setCookie(COOKIES.isEmailVerified, 1, {
                        ...DefaultCookieOptions
                        // maxAge: ENV === "production" ? 30 * 60 : 5 * 60
                    });
                } else {
                    removeCookie(COOKIES.isEmailVerified, DefaultCookieOptions);
                }
                setContext((state) => ({ ...state, isEmailVerified: isEmailVerified }));
            },

            isRecoverEmailVerified: !!+cookies.rev,
            setIsRecoverEmailVerified: (isRecoveryEmailVerified) => {
                if (isRecoveryEmailVerified) {
                    setCookie(COOKIES.isRecoveryEmailVerified, 1, {
                        ...DefaultCookieOptions
                        // maxAge: ENV === "production" ? 30 * 60 : 5 * 60
                    });
                } else {
                    removeCookie(COOKIES.isRecoveryEmailVerified, DefaultCookieOptions);
                }
                setContext((state) => ({ ...state, isRecoverEmailVerified: isRecoveryEmailVerified }));
            },

            ...(src === "csDashboard" &&
                ({
                    csDashboardUserRoles: cookies.csdsbUserRoles,
                    csDashboardPermission: cookies[COOKIES.csDashboardPermission],
                    setCsDashboardUserRoles: (csDashboardUserRoles) => {
                        if (csDashboardUserRoles) {
                            setCookie(COOKIES.csDashboardUserRoles, csDashboardUserRoles, {
                                ...DefaultCookieOptions
                                // maxAge: ENV === "production" ? 30 * 60 : 5 * 60
                            });
                        } else {
                            removeCookie(COOKIES.csDashboardUserRoles, DefaultCookieOptions);
                        }
                        setContext((state) => ({ ...state, csDashboardUserRoles: csDashboardUserRoles }));
                    },
                    setCsDashboardPermission: (csDashboardPermission) => {
                        if (csDashboardPermission) {
                            setCookie(COOKIES.csDashboardPermission, csDashboardPermission, DefaultCookieOptions);
                        } else {
                            removeCookie(COOKIES.csDashboardPermission, DefaultCookieOptions);
                        }
                        setContext((state) => ({ ...state, csDashboardPermission }));
                    },
                    customerCompanyId: cookies.ccid,
                    setCustomerCompanyId: (customerCompanyId) => {
                        if (customerCompanyId) {
                            setCookie(COOKIES.customerCompanyId, customerCompanyId, {
                                ...DefaultCookieOptions
                                // maxAge: ENV === "production" ? 30 * 60 : 5 * 60
                            });
                        } else {
                            removeCookie(COOKIES.customerCompanyId, DefaultCookieOptions);
                        }
                        setContext((state) => ({ ...state, customerCompanyId: customerCompanyId }));
                    },
                    customerCompanyLocation: cookies.ccloc,
                    setCustomerCompanyLocation: (customerCompanyLocation) => {
                        if (customerCompanyLocation) {
                            setCookie(COOKIES.customerCompanyLocation, customerCompanyLocation, {
                                ...DefaultCookieOptions
                                // maxAge: ENV === "production" ? 30 * 60 : 5 * 60
                            });
                        } else {
                            removeCookie(COOKIES.customerCompanyLocation, DefaultCookieOptions);
                        }
                        setContext((state) => ({ ...state, customerCompanyLocation: customerCompanyLocation }));
                    },
                    customerPhone: cookies.cpn,
                    setCustomerPhone: (customerPhone) => {
                        if (customerPhone) {
                            setCookie(COOKIES.customerPhone, customerPhone, {
                                ...DefaultCookieOptions
                                // maxAge: ENV === "production" ? 30 * 60 : 5 * 60
                            });
                        } else {
                            removeCookie(COOKIES.customerPhone, DefaultCookieOptions);
                        }
                        setContext((state) => ({ ...state, customerPhone: customerPhone }));
                    },
                    customerName: cookies.cname,
                    setCustomerName: (customerName) => {
                        if (customerName) {
                            setCookie(COOKIES.customerName, customerName, {
                                ...DefaultCookieOptions
                                // maxAge: ENV === "production" ? 30 * 60 : 5 * 60
                            });
                        } else {
                            removeCookie(COOKIES.customerName, DefaultCookieOptions);
                        }
                        setContext((state) => ({ ...state, customerName: customerName }));
                    },
                    customerServiceType: cookies.cst,
                    setCustomerServiceType: (customerServiceType) => {
                        if (customerServiceType) {
                            setCookie(COOKIES.customerServiceType, customerServiceType, {
                                ...DefaultCookieOptions
                                // maxAge: ENV === "production" ? 30 * 60 : 5 * 60
                            });
                        } else {
                            removeCookie(COOKIES.customerServiceType, DefaultCookieOptions);
                        }
                        setContext((state) => ({ ...state, customerServiceType: customerServiceType }));
                    },
                    tsoLorrySpecialization: undefined,
                    setTsoLorrySpecialization: (tsoLorrySpecialization) => {
                        setContext((state) => ({ ...state, tsoLorrySpecialization }));
                    }
                } as IAppContext)),

            numOfContactRqsts: +cookies.numOfContactRqsts,
            updateNumOfContactRqsts: () => {
                setContext((state) => {
                    const newNum = (state.numOfContactRqsts || 0) + 1;
                    const d = new Date();
                    setCookie("numOfContactRqsts", newNum, {
                        ...DefaultCookieOptions,
                        expires:
                            ENV === "production"
                                ? new Date(new Date(d.setDate(d.getDate() + 1)).setHours(0))
                                : undefined,
                        maxAge: ENV !== "production" ? 60 : undefined
                    });
                    return { ...state, numOfContactRqsts: newNum };
                });
            },
            updateTransporterContactsRqstd: (newItem) => {
                setContext((state) => {
                    const newArr = state.transporterContactsRqstd
                        ? [...state.transporterContactsRqstd, newItem]
                        : [newItem];
                    const d = new Date();
                    setCookie("tcr", JSON.stringify(newArr), {
                        ...DefaultCookieOptions,
                        expires:
                            ENV === "production"
                                ? new Date(new Date(d.setDate(d.getDate() + 1)).setHours(0))
                                : undefined,
                        maxAge: ENV !== "production" ? 60 : undefined
                    });
                    return { ...state, transporterContactsRqstd: newArr };
                });
            },
            updateProfilesViewed: (newProfileId) => {
                setContext((state) => {
                    const newArr = state.profilesViewed ? [...state.profilesViewed, newProfileId] : [newProfileId];
                    setCookie("pv", JSON.stringify(newArr), {
                        ...DefaultCookieOptions
                    });
                    return { ...state, profilesViewed: newArr };
                });
            },
            serviceType: cookies.st || undefined,
            setServiceType: (serviceType) => {
                if (serviceType) {
                    setCookie(COOKIES.serviceType, serviceType, DefaultCookieOptions);
                } else {
                    removeCookie(COOKIES.serviceType, DefaultCookieOptions);
                }
                setContext((state) => ({ ...state, serviceType: serviceType }));
            },
            setKycPopupInitialLoad: () => {
                setContext((state) => {
                    let temp;

                    if (Number(state.kycPopupInitialLoad) < 2) {
                        temp = Number(state.kycPopupInitialLoad) + 1;

                        setCookie(COOKIES.kycPopupInitialLoad, temp, {
                            ...DefaultCookieOptions,
                            expires: undefined,
                            maxAge: undefined
                        });
                    } else if (Number(state.kycPopupInitialLoad) === 2) {
                        setCookie(COOKIES.kycPopupInitialLoad, Number(state.kycPopupInitialLoad) + 1, {
                            ...DefaultCookieOptions,
                            expires: new Date(Date.now() + 24 * 60 * 60 * 5 * 1000),
                            maxAge: ENV !== "production" ? 60 : undefined
                        });
                    }
                    return { ...state, kycPopupInitialLoad: temp };
                });
            },
            setHistoryLocation: (historyLocation) => {
                setContext((state) => {
                    setCookie(COOKIES.historyLocation, historyLocation, {
                        ...DefaultCookieOptions,
                        expires: undefined,
                        maxAge: undefined
                    });
                    return { ...state, historyLocation: historyLocation };
                });
            },
            shouldPreventLayoutSwitch: context.shouldPreventLayoutSwitch,
            setShouldPreventLayoutSwitch: (shouldPreventLayoutSwitch) => {
                setContext((state) => ({ ...state, shouldPreventLayoutSwitch: shouldPreventLayoutSwitch }));
            },
            fromFlutterApp: cookies.fromFlutterApp || undefined,
            setUserAppVersion: (version) => {
                setContext((state) => ({ ...state, userAppVersion: version }));
            },
            setShowAppRedirectionPopup: (val, props) => {
                setContext((state: any) => ({
                    ...state,
                    showAppRedirectionPopup: val,
                    appRedirectionPopupProps: val ? props : undefined
                }));
            },
            setGlobalData: (data: GenericObject) => {
                setContext((state) => ({
                    ...state,
                    globalData: {
                        ...state?.globalData,
                        ...data
                    }
                }));
            },
            updateEventCount,
            checkAndHandleGuestUserRestriction,
            referralCode: cookies.rfc || undefined,
            setReferralCode: (referralCode) => {
                const result = new Date();
                result.setDate(result.getDate() + 13); // 14 days expiry time

                if (referralCode) {
                    setCookie(COOKIES.referralCode, referralCode, {
                        ...DefaultCookieOptions,
                        expires: result
                    });
                } else {
                    removeCookie(COOKIES.referralCode, DefaultCookieOptions);
                }
                setContext((state) => ({ ...state, referralCode: referralCode }));
            },
            updateRoute: (route) => {
                setContext((state) => ({ ...state, route }));
            },
            activateAppMandatePopup: () => {
                setContext((state) => ({ ...state, showAppMandatePopup: true }));
            },
            sessionId: +cookies[COOKIES.sessionId] || 0,
            setSessionId: (sessionId?: number) => {
                sessionId
                    ? setCookie(COOKIES.sessionId, sessionId, DefaultCookieOptions)
                    : removeCookie(COOKIES.sessionId, DefaultCookieOptions);
                setContext((state) => ({ ...state, sessionId }));
            },
            serviceRequestType: context.serviceRequestType,
            setServiceRequestType: (serviceRequestType?: ServiceRequestType) => {
                setContext((state) => ({ ...state, serviceRequestType }));
            },
            setThirdPartyCompanyName: (companyName) => {
                if (companyName) {
                    setCookie(COOKIES.thirdPartyCompanyName, companyName, {
                        ...DefaultCookieOptions
                    });
                } else {
                    removeCookie(COOKIES.thirdPartyCompanyName, DefaultCookieOptions);
                }
                setContext((state) => ({ ...state, thirdPartyCompanyName: companyName }));
            },
            isLorryNotAllowedPopupOpen: false,
            toggleIsLorryNotAllowedPopupOpen: () => {
                setContext((state) => ({ ...state, isLorryNotAllowedPopupOpen: !state.isLorryNotAllowedPopupOpen }));
            }
        }));
    }, []);

    return { context, setContext };
}
